import { EditOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  Descriptions,
  Divider,
  Input,
  Popconfirm,
  Tag,
} from "antd";
import { Add, Delete } from "components/UI";
import Confirm from "components/UI/button/Confirm";
import Skeleton from "components/UI/skeleton";
import dayjs from "dayjs";
import ScheduleReminderButton from "../AllSchedule/Table/ScheduleReminderButton";
import ScheduleStatusSelect from "../AllSchedule/Table/ScheduleStatusSelect";
import ContactDetailLink from "../ContactDetailLink";
import MeetingTagEditor from "../Tags/MeetingTagEditor";
import {
  showAvailableSlotUi,
  showBookedScheduleUi,
} from "../AllSchedule/Table/scheduleRowFields";
import {
  useDeleteMSSchedule,
  useMSScheduleDetail,
  usePatchMSScheduleNote,
  usePatchMSScheduleStatus,
} from "queries/meetingSchedule";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import PopConfirmationUnavailable from "./MultiplePopConfirmation/PopConfirmationUnavailable";
import PopConfirmationAvailable from "./MultiplePopConfirmation/PopConfirmationAvailable";
import { useAntdMessage } from "hooks/useAntdMessage";
import { cn } from "utils/helper";
import MeetingReminderSendDrawer from "./MeetingReminderSendDrawer";
import MeetingReminderListModal from "./MeetingReminderListModal";
import ScheduleChangelogModal from "./ScheduleChangelogModal";
import {
  resolveScheduleContactId,
  resolveSchedulePhoneNumberId,
} from "./scheduleDetailResolvers";

const { TextArea } = Input;

const ContentDetailScheduleTime = ({
  data,
  setAvailabilityFn = () => {},
  hideDetailScheduleTime = () => {},
  agent,
  refetch,
  showEditModal,
  scheduleStatuses,
  scheduleStatusesLocale,
  onScheduleRowPatch,
}) => {
  const { message } = useAntdMessage();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const deleteScheduleMutation = useDeleteMSSchedule();
  const updateNoteMutation = usePatchMSScheduleNote();
  const patchStatusMutation = usePatchMSScheduleStatus();
  const isPast =
    dayjs(data.date).isBefore(dayjs(), "day") ||
    (dayjs(data.date).isSame(dayjs(), "day") &&
      dayjs(`${data.date} ${data.end_time}`).isBefore(dayjs()));

  const { data: scheduleDetail, isLoading } = useMSScheduleDetail(data.id, {
    enabled: !!data.id,
  });

  const mergedRecord = useMemo(
    () => ({ ...(data || {}), ...(scheduleDetail || {}) }),
    [data, scheduleDetail]
  );

  const showBooked = useMemo(
    () =>
      showBookedScheduleUi({
        data,
        scheduleDetail,
        scheduleStatuses,
      }),
    [data, scheduleDetail, scheduleStatuses]
  );

  const showAvailable = useMemo(
    () =>
      showAvailableSlotUi({
        data,
        scheduleDetail,
        scheduleStatuses,
      }),
    [data, scheduleDetail, scheduleStatuses]
  );

  const [stateLoading, setStateLoading] = useState(false);
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [draftNote, setDraftNote] = useState("");
  const [openReminderDrawer, setOpenReminderDrawer] = useState(false);
  const [openReminderList, setOpenReminderList] = useState(false);
  const [openChangelog, setOpenChangelog] = useState(false);

  const canSendReminder = useMemo(() => {
    if (!showBooked || !scheduleDetail || isLoading) return false;
    return (
      resolveSchedulePhoneNumberId(scheduleDetail, data) != null &&
      resolveScheduleContactId(scheduleDetail) != null
    );
  }, [showBooked, scheduleDetail, isLoading, data]);

  useEffect(() => {
    setIsEditingNote(false);
    setOpenReminderDrawer(false);
    setOpenReminderList(false);
  }, [data.id]);

  useEffect(() => {
    if (!isEditingNote && scheduleDetail) {
      setDraftNote(scheduleDetail.note ?? "");
    }
  }, [scheduleDetail, isEditingNote]);

  const callbackLoading = useCallback(() => {
    setStateLoading(false);
    hideDetailScheduleTime();
  }, [hideDetailScheduleTime]);

  const setAvailability = useCallback(
    (available = false, for_all_admin = false, callback, note = null) => {
      const combinedCallback = () => {
        callbackLoading();
        if (typeof callback === "function") {
          callback();
        }
      };
      setStateLoading(true);
      setAvailabilityFn({
        callback: combinedCallback,
        time: {
          start_time: dayjs(data.start_time, "HH:mm:ss").format("HH:mm"),
          end_time: dayjs(data.end_time, "HH:mm:ss").format("HH:mm"),
        },
        is_available: available,
        for_all_admin,
        note,
      });
    },
    [callbackLoading, data, setAvailabilityFn]
  );

  const startEditNote = useCallback(() => {
    if (isPast) return;
    setDraftNote(scheduleDetail?.note ?? "");
    setIsEditingNote(true);
  }, [isPast, scheduleDetail?.note]);

  const handleNoteBlur = useCallback(() => {
    if (isPast || !scheduleDetail?.id) return;
    const trimmed = (draftNote ?? "").trim();
    const prev = (scheduleDetail.note ?? "").trim();
    if (trimmed === prev) {
      setIsEditingNote(false);
      return;
    }
    updateNoteMutation.mutate(
      { id: scheduleDetail.id, note: trimmed },
      {
        onSuccess: (response) => {
          if (response.data.success) {
            if (typeof refetch === "function") {
              refetch();
            }
            message.success(t("meeting_management.note_updated"));
            setIsEditingNote(false);
          } else {
            setDraftNote(scheduleDetail.note ?? "");
            setIsEditingNote(false);
            message.error(
              response.data?.message || t("meeting_management.note_update_failed")
            );
          }
        },
        onError: (error) => {
          setDraftNote(scheduleDetail.note ?? "");
          setIsEditingNote(false);
          if (error.response?.data?.message) {
            message.error(error.response.data.message);
          } else {
            message.error(t("meeting_management.note_update_failed"));
          }
        },
      }
    );
  }, [draftNote, isPast, message, refetch, scheduleDetail, t, updateNoteMutation]);

  const deleteSchedule = useCallback(() => {
    if (!scheduleDetail?.id) return;
    deleteScheduleMutation.mutate(scheduleDetail.id, {
      onSuccess: (response) => {
        if (response.data.success) {
          const dateMonth = dayjs(data.date, "YYYY-MM-DD")
            .startOf("month")
            .format("YYYY-MM-DD");
          if (typeof refetch === "function") {
            refetch();
          }
          queryClient.invalidateQueries({
            queryKey: ["MSCalendarMonth", dateMonth],
          });
          queryClient.invalidateQueries({
            queryKey: ["MSCalendarDay", data.date],
          });
          message.success(t("meeting_management.success_delete_schedule"));
          hideDetailScheduleTime();
        }
      },
      onError: (error) => {
        if (error.response?.data?.message) {
          message.error(error.response.data.message);
        }
      },
    });
  }, [
    t,
    deleteScheduleMutation,
    queryClient,
    data,
    scheduleDetail,
    hideDetailScheduleTime,
    refetch,
    message,
  ]);

  const noteReadKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        startEditNote();
      }
    },
    [startEditNote]
  );

  const handleStatusPatchSuccess = useCallback(
    (patch) => {
      onScheduleRowPatch?.(patch);
    },
    [onScheduleRowPatch]
  );

  const openEditSchedule = useCallback(() => {
    // Form edit memakai data baris (row), yang field `contact`-nya hanya nama
    // tampilan. Suntikkan objek kontak lengkap (+ id) dari detail agar dropdown
    // Kontak ter-isi otomatis saat Ubah Jadwal.
    if (scheduleDetail?.contact) {
      onScheduleRowPatch?.({
        contact: scheduleDetail.contact,
        contact_id: resolveScheduleContactId(scheduleDetail),
      });
    }
    showEditModal({
      mode: "edit",
      step: 0,
      steps: ["schedule", "detail"],
    });
  }, [scheduleDetail, onScheduleRowPatch, showEditModal]);

  return (
    <>
      {isLoading ? (
        <Skeleton active />
      ) : (
        <div className="space-y-6">
          <div className="flex flex-wrap items-end justify-between gap-4 border-b border-neutral-100 pb-4">
            <div className="min-w-0 flex-1 space-y-1">
              {scheduleStatuses?.length > 0 && mergedRecord?.id ? (
                <>
                  <p className="mb-0 text-xs font-medium text-neutral-500">
                    {t("meeting_management.schedule_status")}
                  </p>
                  <ScheduleStatusSelect
                    record={mergedRecord}
                    statuses={scheduleStatuses}
                    locale={scheduleStatusesLocale}
                    patchStatusMutation={patchStatusMutation}
                    onStatusPatchSuccess={handleStatusPatchSuccess}
                    selectClassName="min-w-[200px] max-w-full !text-black"
                  />
                </>
              ) : null}
            </div>
            <Tag color="#f80" className="m-0 shrink-0 text-sm">
              {scheduleDetail?.schedule_admin_name || agent || ""}
            </Tag>
          </div>

          {showBooked ? (
            <Descriptions
              column={1}
              size="small"
              bordered
              className="[&_.ant-descriptions-item-label]:w-[140px] [&_.ant-descriptions-item-label]:bg-neutral-50"
            >
              <Descriptions.Item label={t("name")}>
                <ContactDetailLink
                  contactId={resolveScheduleContactId(scheduleDetail)}
                >
                  {scheduleDetail?.contact?.name}
                </ContactDetailLink>
              </Descriptions.Item>
              <Descriptions.Item label={t("company")}>
                <ContactDetailLink
                  contactId={resolveScheduleContactId(scheduleDetail)}
                >
                  {scheduleDetail?.contact?.company}
                </ContactDetailLink>
              </Descriptions.Item>
              <Descriptions.Item label={t("phone")}>
                {scheduleDetail?.contact?.phone ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label={t("email")}>
                {scheduleDetail?.contact?.email ?? "—"}
              </Descriptions.Item>
            </Descriptions>
          ) : (
            <div>
              <Tag
                color={showAvailable ? "#7bf1a8" : "#D2D2D2"}
                className="text-black"
              >
                {showAvailable
                  ? t("meeting_management.schedule_slot_available")
                  : t("meeting_management.schedule_slot_unavailable")}
              </Tag>
            </div>
          )}

          {showBooked && scheduleDetail?.id && (
            <div className="space-y-1">
              <p className="mb-0 text-xs font-medium text-neutral-500">
                {t("meeting_management.tags")}
              </p>
              <MeetingTagEditor
                scheduleId={scheduleDetail.id}
                tags={scheduleDetail.tags}
              />
            </div>
          )}

          {showBooked && (
            <Card
              size="small"
              title={
                <div className="flex items-center gap-2">
                  <span className="font-semibold">
                    {t("meeting_management.description")}
                  </span>
                  {!isPast && (
                    <button
                      type="button"
                      className="flex items-center text-neutral-500 hover:text-primary"
                      onClick={startEditNote}
                      aria-label={t("meeting_management.edit_schedule")}
                    >
                      <EditOutlined />
                    </button>
                  )}
                </div>
              }
              className="border-neutral-200 shadow-none"
              styles={{ body: { paddingTop: 12 } }}
            >
              {isEditingNote && !isPast ? (
                <TextArea
                  value={draftNote}
                  onChange={(e) => setDraftNote(e.target.value)}
                  onBlur={handleNoteBlur}
                  autoFocus
                  rows={4}
                  disabled={updateNoteMutation.isPending}
                />
              ) : (
                <div
                  role={isPast ? undefined : "button"}
                  tabIndex={isPast ? undefined : 0}
                  onClick={isPast ? undefined : startEditNote}
                  onKeyDown={isPast ? undefined : noteReadKeyDown}
                  className={cn(
                    "min-h-[1.5rem] whitespace-pre-line rounded-md border border-transparent px-1 py-1 text-sm leading-relaxed",
                    !isPast &&
                      "cursor-pointer hover:border-neutral-200 hover:bg-neutral-50"
                  )}
                >
                  {scheduleDetail?.note ? (
                    scheduleDetail.note
                  ) : (
                    <span className="text-neutral-400">
                      {isPast
                        ? "—"
                        : t("meeting_schedule.description_placeholder")}
                    </span>
                  )}
                </div>
              )}
            </Card>
          )}

          {showBooked && scheduleDetail?.report?.trim() && (
            <Card
              size="small"
              title={
                <div className="flex items-center gap-2">
                  <span className="font-semibold">
                    {t("meeting_management.meeting_report")}
                  </span>
                </div>
              }
              className="border-neutral-200 shadow-none"
              styles={{ body: { paddingTop: 12 } }}
            >
              <div className="min-h-[1.5rem] whitespace-pre-line rounded-md px-1 py-1 text-sm leading-relaxed">
                {scheduleDetail.report}
              </div>
            </Card>
          )}

          {showBooked && (canSendReminder || scheduleDetail?.id) && (
            <div className="flex flex-wrap items-center gap-3">
              {canSendReminder && (
                <ScheduleReminderButton
                  onClick={() => setOpenReminderDrawer(true)}
                >
                  {t("meeting_management.send_reminder")}
                </ScheduleReminderButton>
              )}
              {scheduleDetail?.id && (
                <Button
                  size="middle"
                  icon={<UnorderedListOutlined />}
                  onClick={() => setOpenReminderList(true)}
                >
                  {t("meeting_management.view_reminders")}
                </Button>
              )}
            </div>
          )}

          {showBooked && scheduleDetail?.last_modified_by && (
            <button
              type="button"
              onClick={() => setOpenChangelog(true)}
              className="block bg-transparent border-0 p-0 text-left text-xs text-neutral-400 hover:text-primary hover:underline transition-colors cursor-pointer"
            >
              {t("meeting_management.last_modified_by", {
                name: scheduleDetail.last_modified_by,
                time: dayjs(scheduleDetail.last_modified_at).format(
                  "DD MMM YYYY HH:mm"
                ),
              })}
            </button>
          )}

          {!isPast && (
            <>
              <Divider className="my-0" />
              <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                {showBooked ? (
                  <>
                    <Popconfirm
                      title={t("meeting_management.title_delete_schedule")}
                      description={t(
                        "meeting_management.title_delete_schedule_desc"
                      )}
                      onConfirm={deleteSchedule}
                      okText={t("button.yes")}
                      cancelText={t("button.no")}
                      disabled={deleteScheduleMutation.isPending}
                    >
                      <Delete loading={deleteScheduleMutation.isPending} />
                    </Popconfirm>

                    <Confirm
                      title={t("meeting_management.edit_schedule")}
                      icon={<EditOutlined />}
                      onClick={openEditSchedule}
                    />
                  </>
                ) : showAvailable ? (
                  <>
                    <PopConfirmationUnavailable
                      adminName={agent}
                      isLoading={stateLoading}
                      onSubmit={setAvailability}
                    />
                    <Add
                      title={t("meeting_management.add_schedule")}
                      onClick={() =>
                        showEditModal({
                          mode: "create",
                          step: 0,
                          steps: ["group", "detail"],
                        })
                      }
                    />
                  </>
                ) : (
                  <>
                    <div />
                    <PopConfirmationAvailable
                      adminName={agent}
                      isLoading={stateLoading}
                      onSubmit={setAvailability}
                    />
                    <div />
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}
      <MeetingReminderSendDrawer
        open={openReminderDrawer}
        onClose={() => setOpenReminderDrawer(false)}
        scheduleDetail={scheduleDetail}
        timeSource={mergedRecord}
      />
      <ScheduleChangelogModal
        open={openChangelog}
        onClose={() => setOpenChangelog(false)}
        scheduleId={scheduleDetail?.id}
      />
      <MeetingReminderListModal
        open={openReminderList}
        onClose={() => setOpenReminderList(false)}
        scheduleId={scheduleDetail?.id}
        startTime={mergedRecord?.start_time}
      />
    </>
  );
};

export default ContentDetailScheduleTime;
